/*
 * Storefront behaviour. No framework, no build step – native Web Components.
 *   <product-form>      AJAX add-to-cart + client-side variant switching
 *   <quantity-input>    +/- steppers
 *   <cart-items>        line quantity/remove via /cart/change.js
 *   <cart-drawer>       slide-out cart, refreshed through the Section Rendering API
 *   <predictive-search> header search suggestions via /search/suggest
 * See docs/arch.md § Front-end architecture.
 */

const debounce = (fn, wait = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

const formatMoney = (cents) =>
  new Intl.NumberFormat(window.theme.locale, { style: 'currency', currency: window.theme.currency }).format(cents / 100);

/**
 * Swap each refreshed section's [data-cart-count-source] root with the fresh HTML
 * returned by the Cart API (`sections` param), then sync the header cart bubble.
 */
function renderCartSections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-count-source]');
    const current = document.getElementById(`shopify-section-${id}`)?.querySelector('[data-cart-count-source]');
    if (fresh && current) current.replaceWith(fresh);
  });
  const source = document.querySelector('cart-drawer [data-cart-count-source]');
  if (source) updateCartCount(Number(source.dataset.cartCountSource));
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.classList.toggle('is-empty', count === 0);
  });
}

function showError(root, message) {
  const el = root.querySelector('[data-form-error]');
  if (!el) return;
  el.textContent = message || '';
  el.hidden = !message;
}

/* ---------- Quantity stepper ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const min = Number(this.input.min || 0);
      const current = Number(this.input.value) || 0;
      const next = button.name === 'plus' ? current + 1 : Math.max(min, current - 1);
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------- Product form ---------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.available = !this.submitButton?.disabled;

    const variantData = this.querySelector('[data-variants]');
    this.variants = variantData ? JSON.parse(variantData.textContent) : null;

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-index]')) this.onOptionChange();
    });
  }

  get scope() {
    return this.closest('.shopify-section') || document;
  }

  onOptionChange() {
    const selected = [...this.querySelectorAll('.variant-picker__option')].map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      fieldset.querySelector('[data-option-label]').textContent = checked ? checked.value : '';
      return checked?.value;
    });
    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    this.applyVariant(variant);
  }

  applyVariant(variant) {
    const { strings } = window.theme;
    const label = this.querySelector('[data-add-label]');
    this.available = Boolean(variant?.available);
    this.submitButton.disabled = !this.available;
    label.textContent = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;
    if (!variant) return;

    this.form.querySelector('[data-variant-id]').value = variant.id;

    const price = this.scope.querySelector('[data-price]');
    const compare = this.scope.querySelector('[data-compare-price]');
    const salePct = Number(price?.closest('[data-price-wrapper]')?.dataset.salePct || 0);
    let current = variant.price;
    let was = variant.compare_at_price;
    if (salePct && !(was > current)) {
      was = current;
      current = Math.floor((current * (100 - salePct)) / 100);
    }
    const onSale = was > current;
    if (price) price.textContent = formatMoney(current);
    if (compare) {
      compare.textContent = onSale ? formatMoney(was) : '';
      compare.hidden = !onSale;
      compare.closest('[data-price-wrapper]')?.classList.toggle('price--on-sale', onSale);
    }

    if (variant.featured_media) {
      this.scope
        .querySelector(`[data-media-id="${variant.featured_media.id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    if (this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute('aria-busy') === 'true') return;

    showError(this, '');
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.disabled = true;

    const body = new FormData(this.form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        showError(this, data.description || data.message || window.theme.strings.cartError);
        return;
      }
      renderCartSections(data.sections);
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.dataset.openOnAdd === 'true') drawer.open(this.submitButton);
    } catch (error) {
      console.error(error);
      showError(this, window.theme.strings.cartError);
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.submitButton.disabled = !this.available;
    }
  }
}
customElements.define('product-form', ProductForm);

/* ---------- Cart lines (drawer + cart page) ---------- */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.pending = new Map();
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-line-quantity]')) return;
      const line = event.target.closest('[data-line-key]');
      this.queueUpdate(line.dataset.lineKey, Number(event.target.value));
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-line-remove]');
      if (!remove) return;
      this.updateLine(remove.closest('[data-line-key]').dataset.lineKey, 0);
    });
  }

  queueUpdate(key, quantity) {
    if (!this.pending.has(key)) this.pending.set(key, debounce((q) => this.updateLine(key, q), 350));
    this.pending.get(key)(quantity);
  }

  async updateLine(key, quantity) {
    this.classList.add('is-loading');
    showError(this, '');
    try {
      const response = await fetch(`${window.theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.dataset.sections.split(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        showError(this, data.description || data.message || window.theme.strings.cartError);
        return;
      }
      renderCartSections(data.sections);
      document.querySelector('cart-drawer .cart-drawer__panel')?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      showError(this, window.theme.strings.cartError);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('cart-items', CartItems);

/* ---------- Cart drawer ---------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
    // Header cart icon opens the drawer everywhere except the cart page itself.
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (!toggle || document.body.classList.contains('template-cart')) return;
      event.preventDefault();
      this.open(toggle);
    });
  }

  open(trigger) {
    this.trigger = trigger || document.activeElement;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.querySelector('.cart-drawer__panel').focus());
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.trigger?.focus();
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Predictive search ---------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.predictive-search__results');
    this.input.addEventListener('input', debounce(() => this.search(), 250));
    this.closest('details')?.addEventListener('toggle', (event) => {
      if (event.target.open) this.input.focus();
    });
  }

  async search() {
    const terms = this.input.value.trim();
    this.controller?.abort();
    if (!terms) {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }
    this.controller = new AbortController();
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product,page',
      'resources[limit]': '6',
      'resources[options][prefix]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('.predictive-search');
      this.results.innerHTML = content ? content.outerHTML : '';
      this.input.setAttribute('aria-expanded', String(Boolean(content)));
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- <details data-drawer>: Escape / outside click closes ---------- */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[data-drawer][open]').forEach((d) => {
    d.removeAttribute('open');
    d.querySelector('summary')?.focus();
  });
});
document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-drawer][open]').forEach((d) => {
    if (!d.contains(event.target)) d.removeAttribute('open');
  });
});
